
import { useState } from 'react';
import SchemaOrg from '../../components/SEO/SchemaOrg';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState({
    show: false,
    message: '',
    type: '',
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate required fields
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    const email = formData.get('email') as string;
    const inquiryType = formData.get('inquiryType') as string;
    const message = formData.get('message') as string;

    if (!firstName || !lastName || !email || !inquiryType || !message) {
      setFormStatus({
        show: true,
        message: 'Please fill in all required fields.',
        type: 'error',
      });
      return;
    }

    // Validate message length
    if (message.length > 500) {
      setFormStatus({
        show: true,
        message: 'Message must not exceed 500 characters.',
        type: 'error',
      });
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/contact-form`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: new URLSearchParams(formData as any).toString(),
        }
      );

      if (response.ok) {
        setFormStatus({
          show: true,
          message:
            "Thank you for your message! We'll get back to you soon.",
          type: 'success',
        });
        form.reset();
      } else {
        setFormStatus({
          show: true,
          message:
            'Sorry, there was an error sending your message. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        show: true,
        message:
          'Sorry, there was an error sending your message. Please try again.',
        type: 'error',
      });
    }
  };

  // Schema.org data for organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Chef Salad Group",
    "description": "Chef Salad Group creates extraordinary dining experiences across America with award-winning restaurant concepts from fine dining to fast-casual innovation.",
    "url": import.meta.env.VITE_SITE_URL || "https://example.com",
    "logo": import.meta.env.VITE_SITE_URL ? `${import.meta.env.VITE_SITE_URL}/logo.png` : "https://example.com/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "50 Shadow Ridge Rd",
      "addressLocality": "Park City",
      "addressRegion": "UT",
      "postalCode": "84060",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-435-264-1664",
      "contactType": "customer service",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://savouryparkcity.com"
    ]
  };

  const restaurantSchema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": "Chef Salad Group Restaurants",
    "description": "Award-winning restaurant concepts including fine dining destinations, casual neighborhood gems, and fast-casual innovation across America.",
    "url": import.meta.env.VITE_SITE_URL || "https://example.com",
    "servesCuisine": ["American", "Contemporary", "Fine Dining", "Casual Dining"],
    "priceRange": "$-$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "50 Shadow Ridge Rd",
      "addressLocality": "Park City",
      "addressRegion": "UT",
      "postalCode": "84060",
      "addressCountry": "US"
    },
    "telephone": "+1-435-264-1664"
  };

  return (
    <div className="min-h-screen bg-white">
      <SchemaOrg type="organization" data={organizationSchema} />
      <SchemaOrg type="restaurant" data={restaurantSchema} />

      {/* Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-amber-600">
                Chef Salad Group
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  href="#home"
                  className="text-gray-900 hover:text-amber-600 px-3 py-2 text-lg font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  Home
                </a>
                <a
                  href="#about"
                  className="text-gray-700 hover:text-amber-600 px-3 py-2 text-lg font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  About
                </a>
                <a
                  href="#concepts"
                  className="text-gray-700 hover:text-amber-600 px-3 py-2 text-lg font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  Concepts
                </a>
                <a
                  href="#services"
                  className="text-gray-700 hover:text-amber-600 px-3 py-2 text-lg font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  Services
                </a>
                <a
                  href="#contact"
                  className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-full text-lg font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  Contact
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-amber-600 p-2 cursor-pointer"
              >
                <i
                  className={`ri-${
                    isMenuOpen ? 'close' : 'menu'
                  }-line text-2xl`}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t shadow-lg">
              <a
                href="#home"
                className="text-gray-900 hover:text-amber-600 block px-3 py-3 text-lg font-medium cursor-pointer"
              >
                Home
              </a>
              <a
                href="#about"
                className="text-gray-700 hover:text-amber-600 block px-3 py-3 text-lg font-medium cursor-pointer"
              >
                About
              </a>
              <a
                href="#concepts"
                className="text-gray-700 hover:text-amber-600 block px-3 py-3 text-lg font-medium cursor-pointer"
              >
                Concepts
              </a>
              <a
                href="#services"
                className="text-gray-700 hover:text-amber-600 block px-3 py-3 text-lg font-medium cursor-pointer"
              >
                Services
              </a>
              <a
                href="#contact"
                className="text-gray-700 hover:text-amber-600 block px-3 py-3 text-lg font-medium cursor-pointer"
              >
                Contact
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)), url('https://public.readdy.ai/ai/img_res/8b20565c1ac482bb6deb80d71bf9ed6c.jpg')`,
        }}
      >
        <div className="w-full max-w-6xl mx-auto px-4 text-center text-white">
          <h1 className="text-6xl md:text-7xl font-bold mb-8 leading-tight">
            Culinary Excellence
            <br />
            <span className="text-amber-400">Across America</span>
          </h1>
          <p className="text-2xl md:text-3xl mb-10 text-white/90 max-w-4xl mx-auto font-light">
            Where passion meets hospitality. Creating unforgettable dining
            experiences from coast to coast.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="#concepts"
              className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-5 rounded-full text-xl font-semibold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              Discover Our Restaurants
            </a>
            <a
              href="#contact"
              className="border-3 border-white text-white hover:bg-white hover:text-gray-900 px-10 py-5 rounded-full text-xl font-semibold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Join Our Journey
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Our Story</h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Born from a vision to revolutionize dining across America,
              Chef Salad Group brings together the finest culinary minds to create
              extraordinary restaurant experiences.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20diverse%20restaurant%20management%20team%20meeting%20in%20modern%20corporate%20boardroom%2C%20business%20executives%20discussing%20culinary%20strategy%2C%20contemporary%20office%20space%20with%20floor-to-ceiling%20windows%2C%20sophisticated%20business%20environment%20with%20restaurant%20industry%20professionals%20collaborating%20on%20expansion%20plans&width=700&height=500&seq=about2024&orientation=landscape"
                alt="Chef Salad Group Leadership Team"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover object-top"
              />
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Award-Winning Excellence
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Our restaurants have earned recognition across the nation
                    for outstanding food quality, innovative concepts, and
                    exceptional guest experiences.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-global-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Nationwide Presence
                  </h3>
                  <p className="text-gray-600 text-lg">
                    From bustling metropolitan areas to charming small towns,
                    we're committed to bringing exceptional dining to
                    communities across America.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-heart-3-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Community-Centered
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Every restaurant we create becomes a cornerstone of its
                    community, supporting local suppliers and creating
                    meaningful connections.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Our Mission
            </h3>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              To build lasting culinary legacies that bring people together,
              celebrate local flavors, and create moments of joy through
              exceptional food and hospitality. We believe every meal should be
              an experience worth remembering.
            </p>
          </div>
        </div>
      </section>

      {/* Concepts Section */}
      <section id="concepts" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Our Restaurant Concepts
            </h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Each concept in our portfolio represents a unique culinary vision,
              carefully crafted to serve its community with distinction and
              authenticity.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <img
                src="https://readdy.ai/api/search-image?query=Upscale%20fine%20dining%20restaurant%20interior%20with%20elegant%20table%20settings%2C%20sophisticated%20lighting%2C%20luxury%20dining%20room%20with%20contemporary%20design%20elements%2C%20high-end%20restaurant%20atmosphere%20with%20premium%20service%2C%20refined%20culinary%20environment%20with%20artistic%20presentation%20and%20professional%20staff&width=500&height=350&seq=fine2024&orientation=landscape"
                alt="Fine Dining Experience"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Fine Dining Destinations
                </h3>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Sophisticated culinary experiences featuring innovative menus,
                  premium ingredients, and impeccable service in elegant
                  settings.
                </p>
                <div className="flex items-center text-amber-600 font-semibold text-lg cursor-pointer hover:text-amber-700 transition-colors">
                  <a href="https://savouryparkcity.com" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <span>Explore Fine Dining</span>
                    <i className="ri-arrow-right-line ml-3"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20casual%20dining%20restaurant%20with%20comfortable%20seating%2C%20family-friendly%20atmosphere%2C%20neighborhood%20bistro%20interior%20with%20cozy%20lighting%2C%20inviting%20dining%20space%20with%20rustic%20wooden%20elements%2C%20community%20gathering%20place%20with%20relaxed%20sophisticated%20design&width=500&height=350&seq=casual2024&orientation=landscape"
                alt="Casual Dining"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Casual Neighborhood Gems
                </h3>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Welcoming gathering places where families and friends enjoy
                  comfort food elevated with creative touches and warm
                  hospitality.
                </p>
                <div className="flex items-center text-amber-600 font-semibold text-lg cursor-pointer hover:text-amber-700 transition-colors">
                  <a href="https://savouryparkcity.com" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <span>Visit Our Locations</span>
                    <i className="ri-arrow-right-line ml-3"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20fast-casual%20restaurant%20with%20sleek%20counter%20service%20design%2C%20fresh%20healthy%20ingredients%20display%2C%20contemporary%20quick%20service%20dining%20space%20with%20vibrant%20colors%2C%20efficient%20service%20area%20with%20quality%20food%20presentation%2C%20trendy%20urban%20dining%20concept&width=500&height=350&seq=fastcasual2024&orientation=landscape"
                alt="Fast Casual Innovation"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Fast-Casual Innovation
                </h3>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Quick-service concepts that never compromise on quality,
                  delivering fresh, made-to-order meals in vibrant, modern
                  environments.
                </p>
                <div className="flex items-center text-amber-600 font-semibold text-lg cursor-pointer hover:text-amber-700 transition-colors">
                  <a href="https://savouryparkcity.com" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <span>See Our Menus</span>
                    <i className="ri-arrow-right-line ml-3"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 bg-gradient-to-r from-amber-600 to-orange-600 rounded-3xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-6">
              Coming Soon: New Concepts
            </h3>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              We're constantly innovating and expanding our portfolio with
              exciting new dining concepts. Stay tuned for our latest culinary
              adventures!
            </p>
            <a
              href="#contact"
              className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-full text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Be the First to Know
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Restaurant Solutions
            </h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From initial concept to daily operations, we provide end-to-end
              restaurant management services that ensure success at every level.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            <div className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-flash-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Concept Creation
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Developing unique restaurant concepts that capture market
                opportunities and resonate with target demographics through
                innovative design and menu development.
              </p>
            </div>

            <div className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-tools-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Operations Excellence
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Implementing efficient operational systems, staff training
                programs, and quality control measures that ensure consistent
                excellence across all locations.
              </p>
            </div>

            <div className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-palette-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Brand Development
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Creating compelling brand identities and marketing strategies
                that build strong customer loyalty and drive sustainable growth
                in competitive markets.
              </p>
            </div>

            <div className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-smile-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Guest Experience Design
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Crafting memorable dining experiences through thoughtful service
                design, atmosphere creation, and customer journey optimization.
              </p>
            </div>

            <div className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-community-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Community Integration
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Building strong community relationships through local partnerships,
                charitable initiatives, and events that make restaurants integral
                parts of their neighborhoods.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our Impact by the Numbers
            </h2>
            <p className="text-xl text-amber-100">
              Delivering results that matter to our communities and partners
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">2</div>
              <div className="text-amber-100 text-lg">Restaurant Locations</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">2</div>
              <div className="text-amber-100 text-lg">States & Growing</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="text-5xl font-bold text-white mb-2">200K+</div>
              <div className="text-amber-100 text-lg">Satisfied Guests</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Let's Create Something Amazing
            </h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Whether you're interested in partnership opportunities, career
              possibilities, or simply want to learn more about our mission,
              we'd love to connect.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 mb-16">
            <div className="text-center bg-gray-50 rounded-2xl p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Corporate Office
              </h3>
              <p className="text-gray-600 leading-relaxed">
                50 Shadow Ridge Rd<br />
                Park City, UT 84060
              </p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Phone</h3>
              <p className="text-gray-600 leading-relaxed">
                (435) 264-1664<br />
                Monday - Friday<br />
                9:00 AM - 6:00 PM CST
              </p>
            </div>

            <div className="text-center bg-gray-50 rounded-2xl p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Email</h3>
              <p className="text-gray-600 leading-relaxed">
                partnerships@chefsaladgroup.com<br />
                careers@chefsaladgroup.com<br />
                info@chefsaladgroup.com
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-gray-50 rounded-3xl p-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Send Us a Message
              </h3>
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-semibold text-gray-700 mb-3"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-semibold text-gray-700 mb-3"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700 mb-3"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold text-gray-700 mb-3"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label
                    htmlFor="inquiryType"
                    className="block text-sm font-semibold text-gray-700 mb-3"
                  >
                    How Can We Help?
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    required
                    className="w-full px-4 py-4 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white"
                  >
                    <option value="">Select an option</option>
                    <option value="partnership">Partner with us</option>
                    <option value="career">Career opportunity</option>
                    <option value="general">General inquiry</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-700 mb-3"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm bg-white resize-none"
                    placeholder="Enter your message (max 500 characters)"
                  ></textarea>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-3 rounded-full transition-colors"
                  >
                    Send Message
                  </button>
                </div>

                {/* Form status feedback */}
                {formStatus.show && (
                  <div
                    className={`mt-4 p-4 rounded ${
                      formStatus.type === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
